<script setup lang="ts" generic="T">
const props = withDefaults(
  defineProps<{
    icon?: string | null
    title?: string | null
    text?: string | null
    items?: T[]
    itemKey?: (item: T, index: number) => string | number
    itemLabel?: (item: T) => string
  }>(),
  {
    icon: null,
    title: null,
    text: null,
    items: () => [],
    itemKey: (_item: unknown, index: number) => index,
    itemLabel: (item: unknown) => String(item ?? ''),
  },
)

defineSlots<{
  title?(): unknown
  text?(): unknown
  item?(props: { item: T; index: number }): unknown
  default?(): unknown
}>()
</script>

<template>
  <div class="info-notice d-flex">
    <div v-if="icon" class="info-notice__icon">
      <i :class="icon" aria-hidden="true"></i>
    </div>
    <div class="info-notice__body">
      <div v-if="title || $slots.title" class="info-notice__title">
        <slot name="title">{{ title }}</slot>
      </div>
      <div v-if="text || $slots.text" class="info-notice__text">
        <slot name="text">{{ text }}</slot>
      </div>
      <ul v-if="props.items.length" class="info-notice__list">
        <li v-for="(item, index) in props.items" :key="props.itemKey(item, index)" class="info-notice__pill">
          <slot name="item" :item="item" :index="index">{{ props.itemLabel(item) }}</slot>
        </li>
      </ul>
      <slot />
    </div>
  </div>
</template>

<style scoped>
.info-notice {
  --info-notice-bg: #eef2fe;
  --info-notice-accent-bg: #dfe6fd;
  --info-notice-color: #2f3fc4;

  gap: 1rem;
  padding: 1rem 1.25rem;
  background-color: var(--info-notice-bg);
  border-radius: 0.75rem;
  color: var(--info-notice-color);
}

.info-notice__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: var(--info-notice-accent-bg);
}

.info-notice__body {
  min-width: 0;
}

.info-notice__title {
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.info-notice__text {
  font-size: 0.875rem;
}

.info-notice__list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}

.info-notice__pill {
  padding: 0.3rem 0.9rem;
  border-radius: 999px;
  background-color: var(--info-notice-accent-bg);
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
